import { $inject, $type } from "../utils/di-container.js";
import { UrlService } from "./url.service.js";
import { LoggerService } from "./logger.service.js";

/* maybe later we'll want to dynamically load the language texts (e.g using dynamic import),
 * or load from other places but for now there are few texts, and to avoid old browser issues
 * not supporting dynamic imports we just pre-load all known languages here */
import en from '../assets/texts/en.js';
import de from '../assets/texts/de.js';
import fr from '../assets/texts/fr.js';
import es from '../assets/texts/es.js';
import it from '../assets/texts/it.js';
import { replaceAll } from "../utils/string-utils.js";
import { formatDate } from "../utils/date-utils.js";
import { i18nPlurals } from "./i18n.js";
const localTexts = { en, de, fr, es, it };
const templateRegex = new RegExp("{[a-zA-Z]*}", 'ig');

/**
 * @typedef {'en'|'de'|'fr'|'es'|'it'} _Language
 */

export const supportedLanguages = ['en', 'de', 'fr', 'es', 'it'];

/**
 * @typedef {function} _I18nService_fn_getText
 * @param {symbol|string} key
 * @param {Object.<string, string>} [params]
 * @return {string}
 */

/**
 * @typedef {function} _I18nService_fn_getImage
 * @param {string} fileName
 * @return {string}
 */

/**
 * @typedef {object} _I18nResolver
 * @property {_I18nService_fn_getText} getText
 * @property {_I18nService_fn_getImage} getImagePath
 * @property {_I18nService_fn_getImage} getGlobalImagePath
 * @property {function(): string} getCertificateUrl
 * @property {function(productId: string): string} getProductCertificateUrl
 * @property {function(date: Date): string} formatDate
 */

/** @type {_Language} */
export const defaultLanguage = 'de';

/** @type {_Language} */
export const fallbackLanguage = 'en';

/**
 * Implements a simple scheme for internationalization.
 * Allows to load texts and other language-dependant assets (images).
 * Currently, uses texts that are defined locally but can be extended in the future if needed.
 */
export class I18nService {

  /**
   * @private
   * @type {UrlService}
   */
  _urlService;

  /**
   * @private
   * @type {Object.<_Language, Object.<Symbol, string>>}
   */
  _texts = {};

  /**
   * @private
   * @type {Object.<_Language, _I18nResolver>}
   */
  _resolver = {};

  [$inject](obtain) {
    this._logger = obtain(LoggerService);
    this._urlService = obtain(UrlService);
  }

  /**
   * @param {_Language} language
   */
  loadLanguage(language) {
    if (!this._texts[language]) {
      this._texts[language] = localTexts[language];
    }
  }

  /**
   * @param {_Language} language
   * @returns {_I18nResolver}
   */
  forLanguage(language) {
    if (!this._texts[language]) {
      this._logger.warn('global/language-fallback', `Language '${language}' not loaded. Falling back to language: ${fallbackLanguage}`, () => ({ language, fallbackLanguage }));
      language = fallbackLanguage;
    }

    return (this._resolver[language] || (this._resolver[language] = {
      getText: (key, params, counts) => this._processText(language, key, params, counts),
      getImagePath: fileName => `${this._urlService.baseUrl}/trustedretailers/badge2-assets/images/${language}/${fileName}`,
      getGlobalImagePath: fileName => `${this._urlService.baseUrl}/trustedretailers/badge2-assets/images/${fileName}`,
      formatDate: (date, format) => formatDate(language, date, format)
    }));
  }

  /**
   * @private
   * @param {string} language
   * @param {string|symbol} keyOrText
   * @param {Object.<string, string>} params
   * @param {Object.<string, number>} [counts]
   * @returns {string}
   *
   * Returns the translated text for a given key, or text in plain, if text is passed instead.
   * Also allows simple string interpolation in the translated text, by passing parameter values,
   *   by replacing `{key}` placeholders in the actual text with the values provided.
   */
  _processText(language, keyOrText, params, counts) {
    let text = this._texts[language][keyOrText] || keyOrText;
    if (!params) {
      return text;
    }

    // check if all params are non-objects
    const allNonObjects = Object.entries(params).find(([/**/, value]) => typeof (value) === "object") == null;
    if (allNonObjects) {
      return this._doProcessText(language, text, params, counts);
    }

    const children = [];
    let match = null;
    let lastParamIndex = 0;
    while (match = templateRegex.exec(text)) {
      if (match && match.length === 1) {
        const paramName = match[0].substring(1, match[0].length - 1);
        const param = params[paramName];
        // add text from last matched param to this param
        lastParamIndex < match.index && children.push(<span>{text.substring(lastParamIndex, match.index)}</span>);
        // add param value or template value if the param was not provided
        children.push(<span>{param ? param : match[0]}</span>);
        lastParamIndex = match.index + match[0].length;
      }
    }
    children.length > 0 && lastParamIndex < text.length &&
      children.push(<span>{text.substring(lastParamIndex, text.length)}</span>);

    return children.length > 0
      ? (
        <span>
          {children}
        </span>
      )
      : this._doProcessText(language, text, params, counts);
  }

  _doProcessText(language, text, params, counts) {
    /** @type {string} */
    let res = Object.entries(params).reduce((text, [name, value]) => replaceAll(text, `{${name}}`, value), text);

    if (counts) {
      // handle plural usages
      let plural;
      do {
        plural = (/\[(.+)\]/.exec(res) || [])[1];
        if (plural) {
          const countVal = counts[plural] || 0;
          const level = countVal >= 2 ? '2' : countVal.toString(); // 0, 1, or 2
          res = res.replace(/\[(.+)\]/, this._texts[language][i18nPlurals[plural][level]]);
        }
      } while (plural);
    }

    return res;

  }

}

I18nService[$type] = Symbol('I18nService');
